/**
 * STRUCT CODE カレント診断ページ
 * 5問ずつ表示 + i18n対応 + 認証連携
 * 出生地: Nominatim autocomplete（Genesisと同じUI/UX）
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, MapPin, ArrowRight, ArrowLeft, Sparkles, Check, Loader } from 'lucide-react';
import axios from 'axios';
import dynamicStructCodeService from '../services/dynamicStructCodeService';
import { useLanguage } from '../contexts/LanguageContext';

const QUESTIONS_PER_PAGE = 5;

const PageContainer = styled(motion.div)`
  max-width: 640px;
  margin: 0 auto;
  padding: 48px 24px;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 32px 16px;
  }
`;

const HeaderSection = styled.div`
  margin-bottom: 32px;
`;

const Title = styled.h1`
  font-family: ${props => props.theme.fonts.display};
  font-style: italic;
  font-weight: 500;
  font-size: clamp(1.8rem, 4vw, 2.4rem);
  color: ${props => props.theme.colors.text};
  margin: 0 0 10px;
  line-height: 1.2;
`;

const Subtitle = styled.p`
  font-size: 0.92rem;
  line-height: 1.85;
  color: ${props => props.theme.colors.textSecondary};
  margin: 0;
`;

const ProgressContainer = styled.div`
  margin-bottom: 32px;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background: ${props => props.theme.colors.bgPrimary};
  border-radius: 3px;
  overflow: hidden;
`;

const ProgressFill = styled(motion.div)`
  height: 100%;
  background: ${props => props.theme.colors.gradGold};
  border-radius: 3px;
  box-shadow: 0 0 8px rgba(230,199,126,0.35);
`;

const ProgressText = styled.div`
  text-align: right;
  margin-top: 8px;
  font-family: ${props => props.theme.fonts.mono};
  font-size: 0.7rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${props => props.theme.colors.textMuted};
`;

const Card = styled(motion.div)`
  background: ${props => props.theme.colors.bgTertiary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 16px;
  padding: 28px;
  margin-bottom: 16px;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 20px;
  }
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.display};
  font-style: italic;
  font-weight: 500;
  font-size: 1.3rem;
  color: ${props => props.theme.colors.text};
  margin: 0 0 22px;
`;

const FormGroup = styled.div`
  margin-bottom: 24px;
`;

const Label = styled.label`
  display: block;
  font-size: 0.95rem;
  font-weight: 500;
  color: ${props => props.theme.colors.text};
  margin-bottom: 8px;
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 14px;
  font-size: 0.9rem;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 8px;
  transition: all 0.2s;
  font-family: ${props => props.theme.fonts.main};
  background: ${props => props.theme.colors.bgPrimary};
  color: ${props => props.theme.colors.text};
  cursor: pointer;
  appearance: none;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }

  &:disabled { opacity: 0.5; cursor: not-allowed; }

  option { background: ${props => props.theme.colors.bgPrimary}; }
`;

const DateSelectContainer = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 12px;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) { gap: 8px; }
`;

const HelpText = styled.div`
  font-size: 0.8rem;
  color: ${props => props.theme.colors.textSecondary};
  margin-top: 6px;
  line-height: 1.5;
`;

/* Location input with icon */
const LocationInputWrapper = styled.div`
  position: relative;
`;

const LocationInput = styled.input`
  width: 100%;
  padding: 12px 14px 12px 38px;
  font-size: 0.9rem;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 8px;
  transition: all 0.2s;
  font-family: ${props => props.theme.fonts.main};
  background: ${props => props.theme.colors.bgPrimary};
  color: ${props => props.theme.colors.text};
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }

  &::placeholder {
    color: ${props => props.theme.colors.textMuted};
  }
`;

const LocationIconWrapper = styled.div`
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: ${props => props.theme.colors.textSecondary};
  pointer-events: none;
`;

const LocationSpinner = styled.div`
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: ${props => props.theme.colors.textSecondary};
  animation: spin 1s linear infinite;

  @keyframes spin {
    from { transform: translateY(-50%) rotate(0deg); }
    to { transform: translateY(-50%) rotate(360deg); }
  }
`;

const LocationConfirm = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  font-size: 0.85rem;
  color: ${props => props.theme.colors.primary};
  font-weight: 500;
`;

const SuggestionsDropdown = styled.div`
  margin-top: 4px;
  background: ${props => props.theme.colors.bgSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 8px;
  box-shadow: ${props => props.theme.shadows.lg};
  overflow: hidden;
  max-height: 260px;
  overflow-y: auto;
  z-index: 50;
  position: relative;
`;

const SuggestionItem = styled.button`
  width: 100%;
  text-align: left;
  padding: 12px 16px;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: background 0.15s;
  border-bottom: 1px solid ${props => props.theme.colors.borderLight};
  font-family: ${props => props.theme.fonts.main};

  &:last-child { border-bottom: none; }

  &:hover {
    background: ${props => props.theme.colors.background};
  }
`;

const SuggestionShort = styled.div`
  font-size: 0.9rem;
  color: ${props => props.theme.colors.text};
  font-weight: 500;
`;

const SuggestionFull = styled.div`
  font-size: 0.75rem;
  color: ${props => props.theme.colors.textSecondary};
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const QuestionCard = styled(motion.div)`
  background: ${props => props.theme.colors.bgTertiary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 12px;
`;

const QuestionNumber = styled.div`
  font-family: ${props => props.theme.fonts.mono};
  font-size: 0.7rem;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  text-transform: uppercase;
  letter-spacing: 0.22em;
  margin-bottom: 12px;
`;

const QuestionText = styled.div`
  font-size: 1.08rem;
  font-weight: 400;
  color: ${props => props.theme.colors.text};
  margin-bottom: 20px;
  line-height: 1.85;
`;

const ChoicesContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const ChoiceButton = styled(motion.button)`
  width: 100%;
  padding: 14px 18px;
  text-align: left;
  background: ${props => props.selected
    ? props.theme.colors.primaryGlow
    : props.theme.colors.bgPrimary};
  color: ${props => props.theme.colors.text};
  border: 1px solid ${props => props.selected
    ? props.theme.colors.primary
    : props.theme.colors.border};
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.25s ease;
  font-size: 0.88rem;
  font-family: ${props => props.theme.fonts.main};
  line-height: 1.65;
  box-shadow: ${props => props.selected ? '0 0 18px rgba(230,199,126,0.18)' : 'none'};

  &:hover {
    border-color: ${props => props.selected ? props.theme.colors.primary : props.theme.colors.borderHover};
    background: ${props => props.selected ? props.theme.colors.primaryGlow : props.theme.colors.bgHover};
  }
`;

const NavigationButtons = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 32px;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    gap: 12px;
    margin-top: 24px;
  }
`;

const Button = styled(motion.button)`
  flex: 1;
  padding: 15px 30px;
  font-size: 0.92rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  border-radius: ${props => props.theme.borderRadius.full};
  cursor: pointer;
  transition: all 0.25s ease;
  font-family: ${props => props.theme.fonts.main};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  ${props => props.primary ? `
    background: ${props.theme.colors.gradGold};
    color: #1A1408;
    border: 1px solid rgba(230,199,126,0.55);
    box-shadow: ${props.theme.shadows.glowSoft};

    &:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: ${props.theme.shadows.glow};
    }
  ` : `
    background: transparent;
    color: ${props.theme.colors.textSecondary};
    border: 1px solid ${props.theme.colors.lineSoft};

    &:hover {
      color: ${props.theme.colors.starGold};
      border-color: ${props.theme.colors.lineGold};
    }
  `}

  &:disabled { opacity: 0.45; cursor: not-allowed; transform: none; box-shadow: none; }
`;

const LoadingOverlay = styled(motion.div)`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(10, 10, 20, 0.85);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const LoadingCard = styled(motion.div)`
  background: ${props => props.theme.colors.bgTertiary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 16px;
  padding: 40px;
  text-align: center;
  max-width: 360px;
  margin: 0 20px;
`;

const LoadingText = styled.div`
  font-family: ${props => props.theme.fonts.display};
  font-style: italic;
  font-size: 1.3rem;
  font-weight: 500;
  color: ${props => props.theme.colors.text};
  margin-top: 24px;
`;

const LoadingSubText = styled.div`
  font-size: 0.92rem;
  color: ${props => props.theme.colors.textSecondary};
  margin-top: 14px;
  line-height: 1.85;
`;

// ── Nominatim helpers (same logic as Genesis) ──

function buildShortName(item) {
  const addr = item.address || {};
  const parts = [];

  const detail = addr.suburb || addr.neighbourhood || addr.quarter || addr.borough || '';
  const city = addr.city || addr.town || addr.village || addr.municipality || '';
  const state = addr.state || addr.province || addr.county || '';
  const country = addr.country || '';

  if (detail) parts.push(detail);
  if (city && city !== detail) parts.push(city);
  if (state && state !== city) parts.push(state);
  if (country) parts.push(country);

  return parts.join(', ') || (item.display_name || '').split(',').slice(0, 3).join(',') || '';
}

// ── Year/Month/Day helpers ──

const generateYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let y = currentYear - 10; y >= currentYear - 90; y--) {
    years.push(y);
  }
  return years;
};

const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => i + 1);

const generateDayOptions = (year, month) => {
  if (!year || !month) return Array.from({ length: 31 }, (_, i) => i + 1);
  const daysInMonth = new Date(parseInt(year), parseInt(month), 0).getDate();
  return Array.from({ length: daysInMonth }, (_, i) => i + 1);
};

const MONTH_LABELS_JA = ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月'];
const MONTH_LABELS_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function DiagnosisDynamicPage() {
  const navigate = useNavigate();
  const { lang, t } = useLanguage();
  const [step, setStep] = useState('info');
  const [birthYear, setBirthYear] = useState('');
  const [birthMonth, setBirthMonth] = useState('');
  const [birthDay, setBirthDay] = useState('');
  // Location: Nominatim autocomplete
  const [birthLocation, setBirthLocation] = useState('');
  // サジェスト選択時に Nominatim が返した座標。診断リクエストに載せる。
  const [birthCoords, setBirthCoords] = useState(null);
  const [locationQuery, setLocationQuery] = useState('');
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [locationLoading, setLocationLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const locationRef = useRef(null);
  const debounceRef = useRef(null);

  const [answers, setAnswers] = useState({});
  const [currentPage, setCurrentPage] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [, setLoadingQuestions] = useState(true);

  const monthLabels = lang === 'en' ? MONTH_LABELS_EN : MONTH_LABELS_JA;

  // Close suggestions on outside click
  useEffect(() => {
    const handler = (e) => {
      if (locationRef.current && !locationRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Geocode location with Nominatim (debounced)
  const searchLocation = useCallback(async (query) => {
    if (query.length < 2) {
      setLocationSuggestions([]);
      return;
    }
    setLocationLoading(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?` +
        `q=${encodeURIComponent(query)}&format=json&addressdetails=1&limit=6&accept-language=ja,en`,
        { headers: { 'User-Agent': 'StructCode/1.0' } }
      );
      const data = await res.json();
      setLocationSuggestions(
        data.map((item) => ({
          display: item.display_name,
          short: buildShortName(item),
          lat: item.lat,
          lon: item.lon,
        }))
      );
      setShowSuggestions(true);
    } catch {
      setLocationSuggestions([]);
    } finally {
      setLocationLoading(false);
    }
  }, []);

  const handleLocationInput = (value) => {
    setLocationQuery(value);
    setBirthLocation(''); // Clear selection when typing
    setBirthCoords(null); // 選択が解除されたので座標も破棄する
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchLocation(value), 400);
  };

  const selectLocation = (suggestion) => {
    setBirthLocation(suggestion.short);
    setLocationQuery(suggestion.short);
    // サジェストが返した座標を保持して診断リクエストに載せる。
    // これがサーバー側の再ジオコーディングを不要にする。
    setBirthCoords(
      suggestion.lat != null && suggestion.lon != null
        ? { lat: Number(suggestion.lat), lon: Number(suggestion.lon) }
        : null
    );
    setShowSuggestions(false);
  };

  // 質問データをAPIから取得
  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        setLoadingQuestions(true);
        const apiUrl = process.env.REACT_APP_API_URL || 'https://struct-code-production.up.railway.app';
        const response = await axios.get(`${apiUrl}/api/questions?lang=${lang}`);

        const transformedQuestions = response.data.map(q => ({
          id: q.id,
          text: q.question,
          choices: Object.entries(q.choices).map(([key, value]) => ({
            id: key,
            text: value
          }))
        }));

        setQuestions(transformedQuestions);
      } catch (error) {
        console.error('Failed to fetch questions:', error);
        setQuestions([]);
      } finally {
        setLoadingQuestions(false);
      }
    };
    fetchQuestions();
  }, [lang]);

  // Handle year/month change: reset day if out of range
  const handleYearChange = (value) => {
    setBirthYear(value);
    if (birthDay && value && birthMonth) {
      const maxDay = new Date(parseInt(value), parseInt(birthMonth), 0).getDate();
      if (parseInt(birthDay) > maxDay) setBirthDay('');
    }
  };

  const handleMonthChange = (value) => {
    setBirthMonth(value);
    if (birthDay && birthYear && value) {
      const maxDay = new Date(parseInt(birthYear), parseInt(value), 0).getDate();
      if (parseInt(birthDay) > maxDay) setBirthDay('');
    }
  };

  const totalPages = Math.ceil(questions.length / QUESTIONS_PER_PAGE);
  const currentQuestions = questions.slice(
    currentPage * QUESTIONS_PER_PAGE,
    (currentPage + 1) * QUESTIONS_PER_PAGE
  );

  // Progress: info step = 0, question pages = proportional
  const progress = step === 'info'
    ? 0
    : ((currentPage + 1) / totalPages) * 100;

  const handleInfoSubmit = (e) => {
    e.preventDefault();
    if (birthYear && birthMonth && birthDay && birthLocation) {
      setStep('questions');
    }
  };

  const handleChoiceSelect = (questionId, choice) => {
    setAnswers(prev => ({ ...prev, [questionId]: choice }));
  };

  // Check if all questions on current page are answered
  const isCurrentPageComplete = currentQuestions.every(q => answers[q.id] !== undefined);

  const handleNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(prev => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(prev => prev - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      setStep('info');
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      const answersArray = Object.entries(answers).map(([questionId, choice]) => ({
        question_id: questionId,
        choice: choice
      }));

      const birthDate = `${birthYear}-${String(birthMonth).padStart(2, '0')}-${String(birthDay).padStart(2, '0')}`;

      const diagnosisData = {
        birth_date: birthDate,
        birth_location: birthLocation,
        answers: answersArray,
        diagnosis_date: new Date().toISOString(),
        // 出生地サジェストで選択した時点の座標をそのまま送る。
        // 従来は地名の文字列だけを送っていたため、サーバーが同じ Nominatim に
        // 問い合わせ直しており、1 件あたり 2 秒かかる上に外部障害で診断が
        // 止まっていた。選択された地点とズレる可能性もなくなる。
        ...(birthCoords ? { birth_lat: birthCoords.lat, birth_lon: birthCoords.lon } : {})
      };

      const result = await dynamicStructCodeService.createDynamicDiagnosis(diagnosisData);

      navigate(`/current-result/${result.diagnosis_id}`, {
        state: { diagnosis: result }
      });
    } catch (error) {
      console.error('Diagnosis failed:', error);
      alert(t(
        `診断に失敗しました: ${error.message || '不明なエラー'}`,
        `Diagnosis failed: ${error.message || 'Unknown error'}`
      ));
      setIsSubmitting(false);
    }
  };

  const isLastPage = currentPage === totalPages - 1;

  return (
    <PageContainer
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <HeaderSection>
        <Title>
          <Sparkles size={32} />
          {t('内面構造診断', 'Inner Structure Analysis')}
        </Title>
        <Subtitle>
          {t('あなたの「今」の内面構造を診断します', 'Diagnose your current inner structure')}
        </Subtitle>
      </HeaderSection>

      <ProgressContainer>
        <ProgressBar>
          <ProgressFill
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.3 }}
          />
        </ProgressBar>
        {step === 'questions' && (
          <ProgressText>
            {t('ページ', 'Page')} {currentPage + 1} / {totalPages}
          </ProgressText>
        )}
      </ProgressContainer>

      <AnimatePresence mode="wait">
        {step === 'info' && (
          <Card
            key="info"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
          >
            <SectionTitle>
              <Calendar size={24} />
              {t('生年月日・出生地', 'Birth Information')}
            </SectionTitle>

            <HelpText style={{ marginBottom: 20, fontSize: '0.9rem' }}>
              {t(
                '天体配置の計算のため、正確な生年月日と出生地が必要です。',
                'Accurate birth date and location are required for astrological calculation.'
              )}
            </HelpText>

            <form onSubmit={handleInfoSubmit}>
              <FormGroup>
                <Label>{t('生年月日', 'Birth Date')}</Label>
                <DateSelectContainer>
                  <Select value={birthYear} onChange={(e) => handleYearChange(e.target.value)} required>
                    <option value="">{t('年', 'Year')}</option>
                    {generateYearOptions().map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </Select>
                  <Select value={birthMonth} onChange={(e) => handleMonthChange(e.target.value)} required>
                    <option value="">{t('月', 'Month')}</option>
                    {MONTH_OPTIONS.map(m => (
                      <option key={m} value={m}>{monthLabels[m - 1]}</option>
                    ))}
                  </Select>
                  <Select value={birthDay} onChange={(e) => setBirthDay(e.target.value)} required>
                    <option value="">{t('日', 'Day')}</option>
                    {generateDayOptions(birthYear, birthMonth).map(d => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </Select>
                </DateSelectContainer>
              </FormGroup>

              <FormGroup ref={locationRef}>
                <Label>{t('出生地', 'Birth Location')}</Label>
                <HelpText style={{ marginBottom: 10 }}>
                  {t(
                    '市区町村レベルまで指定してください。例: 渋谷区, 福岡市中央区, Manhattan NY',
                    'Please specify down to the city/ward level. Example: Shibuya, Manhattan NY, London'
                  )}
                </HelpText>
                <LocationInputWrapper>
                  <LocationIconWrapper>
                    <MapPin size={16} />
                  </LocationIconWrapper>
                  <LocationInput
                    type="text"
                    value={locationQuery}
                    onChange={(e) => handleLocationInput(e.target.value)}
                    onFocus={() => locationSuggestions.length > 0 && setShowSuggestions(true)}
                    placeholder={t('例: 渋谷区, 福岡市, Manhattan...', 'e.g. Shibuya, Manhattan, London...')}
                  />
                  {locationLoading && (
                    <LocationSpinner>
                      <Loader size={16} />
                    </LocationSpinner>
                  )}
                </LocationInputWrapper>

                {birthLocation && (
                  <LocationConfirm>
                    <Check size={14} />
                    {birthLocation}
                  </LocationConfirm>
                )}

                {showSuggestions && locationSuggestions.length > 0 && (
                  <SuggestionsDropdown>
                    {locationSuggestions.map((s, i) => (
                      <SuggestionItem
                        key={i}
                        onClick={() => selectLocation(s)}
                      >
                        <SuggestionShort>{s.short}</SuggestionShort>
                        <SuggestionFull>{s.display}</SuggestionFull>
                      </SuggestionItem>
                    ))}
                  </SuggestionsDropdown>
                )}
              </FormGroup>

              <Button
                type="submit"
                primary
                disabled={!birthYear || !birthMonth || !birthDay || !birthLocation}
              >
                {t('次へ（質問へ進む）', 'Next (Proceed to Questions)')}
                <ArrowRight size={20} />
              </Button>
            </form>
          </Card>
        )}

        {step === 'questions' && (
          <motion.div
            key={`page-${currentPage}`}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -30 }}
            transition={{ duration: 0.3 }}
          >
            {currentQuestions.map((question, idx) => {
              const globalIdx = currentPage * QUESTIONS_PER_PAGE + idx;
              return (
                <QuestionCard
                  key={question.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                >
                  <QuestionNumber>Q.{globalIdx + 1} / {questions.length}</QuestionNumber>
                  <QuestionText>{question.text}</QuestionText>
                  <ChoicesContainer>
                    {question.choices.map((choice) => (
                      <ChoiceButton
                        key={choice.id}
                        selected={answers[question.id] === choice.id}
                        onClick={() => handleChoiceSelect(question.id, choice.id)}
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.99 }}
                      >
                        <strong>{choice.id}.</strong> {choice.text}
                      </ChoiceButton>
                    ))}
                  </ChoicesContainer>
                </QuestionCard>
              );
            })}

            <NavigationButtons>
              <Button onClick={handlePrevPage}>
                <ArrowLeft size={20} />
                {currentPage === 0 ? t('出生情報に戻る', 'Back to Birth Info') : t('前へ', 'Previous')}
              </Button>
              {isLastPage ? (
                <Button
                  primary
                  onClick={handleSubmit}
                  disabled={!isCurrentPageComplete}
                >
                  {t('診断する', 'Submit Diagnosis')}
                  <Sparkles size={20} />
                </Button>
              ) : (
                <Button
                  primary
                  onClick={handleNextPage}
                  disabled={!isCurrentPageComplete}
                >
                  {t('次へ', 'Next')}
                  <ArrowRight size={20} />
                </Button>
              )}
            </NavigationButtons>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isSubmitting && (
          <LoadingOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <LoadingCard
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
              >
                <Sparkles size={48} color="#6366F1" />
              </motion.div>
              <LoadingText>{t('診断を実行中...', 'Analyzing...')}</LoadingText>
              <LoadingSubText>
                {t(
                  '正確な天体計算を行っています。\n少々お待ちください。',
                  'Performing precise astronomical calculations.\nPlease wait a moment.'
                )}
              </LoadingSubText>
            </LoadingCard>
          </LoadingOverlay>
        )}
      </AnimatePresence>
    </PageContainer>
  );
}

export default DiagnosisDynamicPage;
